import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { formatDateTime } from '@/lib/format';
import { pageCount, useTenantSearch } from './tenants/useTenants';

/**
 * How long the box waits after the last keystroke before asking the API.
 *
 * Longer than the customer app's 250ms, because a request here is not free in
 * the way that one is: `GET /admin/tenants` writes a `tenants.list` audit row
 * carrying the search term (M1-31), so every pause mid-word becomes a
 * permanent row that M1-35's screen has to be readable through. 400ms is the
 * difference between one row per search and three.
 */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Tenant lookup (M1-33): a box, a table, and a link per row.
 *
 * The screen is deliberately plain and read-only. Every matching rule it
 * appears to have belongs to `GET /api/v1/admin/tenants` (M1-31) — a
 * case-insensitive contains on the account name — and the ordering is the
 * API's too.
 *
 * <b>The search term is NOT in the URL</b>, a deliberate departure from the
 * customer app's `/customers?q=`. Telemetry is initialised with
 * `enableAutoRouteTracking`, which sends the page URI — query string included —
 * to Application Insights on every route change, and a staff search term is a
 * half-remembered business name typed off a phone call. A tenant's *id* in
 * `/tenants/{id}` is an identifier and is fine, which is why the link that
 * actually matters — the one pasted into a ticket — is still shareable.
 *
 * The cost is that a search is not itself a link. That is the right trade for
 * a console whose every read is already audited by name on the server.
 *
 * ⚠️ <b>This protects the PAGE VIEW, and nothing else. It is not a telemetry
 * control.</b> M1-33 originally claimed more than that, and the claim was
 * wrong: the SDK also auto-instruments `fetch` and `XHR`
 * (`disableFetchTracking` and `disableAjaxTracking` both default to FALSE) and
 * puts the absolute request URL on the dependency item — so
 * `GET /api/v1/admin/tenants?q=…` reached App Insights the whole time,
 * whatever the address bar said. Keeping state out of the URL is a UX
 * decision that happens to help; `lib/telemetryRedaction.ts` (CC-17) is what
 * actually keeps the term out of telemetry, and it owns that guarantee.
 */
export function Tenants() {
  const { t } = useTranslation();

  // What is in the box, and what has actually been asked for. Separate, so the
  // field stays responsive while the API is asked at most once per pause.
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (term.trim() === query) {
      return;
    }
    const timer = setTimeout(() => {
      setQuery(term.trim());
      // A new search starts at the first page. Without this, refining a term
      // while on page 3 asks for page 3 of a result set that may only have
      // one — an empty table that looks exactly like "no matches".
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, query]);

  const tenants = useTenantSearch(query, page);
  const results = tenants.data?.items ?? [];
  const totalCount = tenants.data?.totalCount ?? 0;
  const totalPages = pageCount(totalCount, tenants.data?.pageSize ?? 0);

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('tenants.title')}</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('tenants.subtitle')}</p>
      </div>

      {/* A real <form> so Enter submits from a phone keyboard's search key.
          Submitting is otherwise a no-op — the debounce has already run — so
          it only prevents the page reload. */}
      <form role="search" onSubmit={(event) => event.preventDefault()}>
        <div className="max-w-md text-sm">
          {/* `htmlFor` plus `aria-describedby`, not a wrapping <label>: a
              wrapper would make the field's accessible name its entire text
              content, hint included. */}
          <label htmlFor="tenant-search" className="mb-1 block font-medium">
            {t('tenants.searchLabel')}
          </label>
          <input
            id="tenant-search"
            type="search"
            aria-describedby="tenant-search-hint"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('tenants.searchPlaceholder')}
            className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-3 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          />
          <span
            id="tenant-search-hint"
            className="mt-1 block text-xs text-[var(--color-muted-foreground)]"
          >
            {t('tenants.searchHint')}
          </span>
        </div>
      </form>

      {tenants.isPending ? (
        <LoadingState label={t('tenants.loading')} />
      ) : tenants.isError ? (
        <ErrorState
          title={t('tenants.loadErrorTitle')}
          description={t('tenants.loadErrorDescription')}
          onRetry={() => void tenants.refetch()}
        />
      ) : results.length === 0 ? (
        // "No matches for that" and "no tenants at all" are different facts,
        // and staff who cannot tell them apart will retype a search that was
        // never going to match.
        <EmptyState
          title={query ? t('tenants.noMatchesTitle') : t('tenants.emptyTitle')}
          description={
            query
              ? t('tenants.noMatchesDescription', { term: query })
              : t('tenants.emptyDescription')
          }
        />
      ) : (
        <>
          {/* Visible AND announced. Search-as-you-type replaces this table
              under someone who may not be able to see it change, and the total
              is worth showing anyway — it is how staff learn that a term like
              "plumbing" matched 400 accounts rather than the one they wanted. */}
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-[var(--color-muted-foreground)]"
          >
            {t('tenants.resultCount', { count: totalCount })}
          </p>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <caption className="sr-only">{t('tenants.tableCaption')}</caption>
              <thead className="border-b bg-[var(--color-muted)]">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('tenants.columnName')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('tenants.columnPlan')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('tenants.columnStatus')}
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-left font-medium sm:table-cell">
                    {t('tenants.columnCreated')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((tenant) => (
                  <tr key={tenant.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2">
                      <Link
                        to={`/tenants/${tenant.id}`}
                        className="font-medium underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                      >
                        {tenant.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-[var(--color-muted-foreground)]">
                      {t(`tenants.plan.${tenant.planTier}`, { defaultValue: tenant.planTier })}
                    </td>
                    <td className="px-4 py-2">
                      {t(`tenants.status.${tenant.status}`, { defaultValue: tenant.status })}
                    </td>
                    {/* The viewer's own clock, date and time together — see the
                        note on TenantDetail's `createdAt`. */}
                    <td className="hidden px-4 py-2 text-[var(--color-muted-foreground)] sm:table-cell">
                      {formatDateTime(tenant.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <nav
              aria-label={t('tenants.pagination')}
              className="flex flex-wrap items-center justify-between gap-3"
            >
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {/* No `count` in the interpolation on purpose: i18next treats
                    that key as a plural selector, and this string has none. */}
                {t('tenants.pageOf', { page, totalPages })}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  {t('tenants.previousPage')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  {t('tenants.nextPage')}
                </Button>
              </div>
            </nav>
          ) : null}
        </>
      )}
    </section>
  );
}
